<template>
    <div
        :class="[
            $mq,
            type,
            {
                'single-label--traceability': showTraceabilityKind,
                'is-expired': isExpired,
                'is-expiring-today': isExpiresToday
            }
        ]"
        class="single-label"
    >
        <span v-if="showTraceabilityKind" class="expiration-rail" aria-hidden="true"></span>
        <div class="image" :style="productImageStyle"></div>
        <div class="info" :class="{ is_printing: is_printing }">
            <div class="name" :title="formatDisplayMeasurement(product.name || '')">
                {{ formatDisplayMeasurement(product.name || '') }}
            </div>

            <template v-if="showTraceabilityKind">
                <div class="card-tags">
                    <span v-if="isExpired" class="status-tag status-tag--expired">
                        <span class="status-tag__dot" aria-hidden="true"></span>
                        {{ sentenceCase($t('label.expired')) }}
                    </span>
                    <span v-else-if="isExpiresToday" class="status-tag status-tag--today">
                        <span class="status-tag__dot" aria-hidden="true"></span>
                        {{ $t('label.expires_today') }}
                    </span>
                    <span v-if="state.name" class="state-tag" :title="state.name">{{ state.name }}</span>
                    <span v-if="showTimerKindIcon" class="quantity-tag" :aria-label="`${$t('label.traceability.record_type_timer')}: ${timerQuantity}`">
                        <i class="i-andy-menu-timer i-andy-s" aria-hidden="true"></i>
                        <span>{{ timerQuantity }}</span>
                    </span>
                    <span v-if="showPhysicalKindIcon" class="quantity-tag" :aria-label="`${$t('label.traceability.record_type_physical')}: ${physicalQuantity}`">
                        <span class="physical-label-icon" aria-hidden="true"></span>
                        <span>{{ physicalQuantity }}</span>
                    </span>
                </div>
                <div class="date">
                    {{ displayDateLabel }} {{ parseDate(displayPrintTime) }}
                </div>
            </template>

            <template v-else>
                <div class="state">
                    {{ state.name || '' }}
                </div>
                <div class="meta">
                    <div class="quantity">{{ physicalQuantity }}</div>
                    <div class="date" v-if="showBy == 'print_date'">{{ $t('label.print_date') + ' ' + parseDate(displayPrintTime) }}</div>
                    <div class="date" :class="{ expired: isExpired }" v-else>
                        {{ $t('label.cad') + ' ' + parseDate(displayPrintTime) }}
                    </div>
                </div>
            </template>
        </div>
        <div v-if="!is_printing" class="action" :class="type" @click="discardLabel()"></div>
    </div>
</template>

<script>
import { formatMeasurementLabel } from '@/utils/numberFormat'

import { formatTraceabilityDate, parseTraceabilityMoment, resolveTraceabilityExpirationState } from '@/utils/traceabilityExpiration'
import { resolveDateDisplayLocale } from '@/utils/dateInput'

export default {
    name: 'singleLabel',
    props: {
        label: {
            type: [Object, Boolean],
            default: false
        },
        type: {
            type: String,
            default: 'discard'
        },
        is_printing: {
            type: Boolean,
            default: false
        },
        showBy: {
            type: String,
            default: false
        },
        showTraceabilityKind: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            queue: {},
            date: {}
        }
    },
    computed: {
        dateLocale() {
            const employee = this.$store.getters['loginUser/getUserInformation'] || {}
            const location = this.$store.getters['loginUser/getLocalLocation'] || {}
            return resolveDateDisplayLocale(this.$i18n.locale, this.$store.getters['login/getConfig'] || {}, employee.lang || employee.language, location.lang, this.$store.getters['intl/getLocale'])
        },
        labels() {
            return this.$store.getters['label/getLabelsOrdered'] || {}
        },
        product() {
            if (!this.label || !this.label.product) {
                return {}
            }
            return this.labels[this.label.product] || {}
        },
        productImageStyle() {
            const image = this.product.local_image || this.product.image
            return image ? { backgroundImage: `url("${image}")` } : {}
        },
        state() {
            if (!this.label) {
                return {}
            }
            return this.$store.getters['label/getLabelStates'](this.label.state) || {}
        },
        resolvedTraceabilityKind() {
            if (!this.label || typeof this.label.traceabilityKind === 'undefined') {
                return ''
            }
            return this.label.traceabilityKind
        },
        showPhysicalKindIcon() {
            const kind = this.resolvedTraceabilityKind
            return kind === 'physical' || kind === 'both'
        },
        showTimerKindIcon() {
            const kind = this.resolvedTraceabilityKind
            return kind === 'timer' || kind === 'both'
        },
        parsedLabelData() {
            if (!this.label || !this.label.data) {
                return {}
            }
            if (typeof this.label.data === 'object') {
                return this.label.data
            }
            try {
                return JSON.parse(this.label.data)
            } catch (e) {
                return {}
            }
        },
        physicalQuantity() {
            return this.normalizeQuantity(this.label && this.label.quantity, this.parsedLabelData.quantity)
        },
        timerQuantity() {
            const available = this.parsedLabelData.quantity_avaibles
            return this.normalizeQuantity(available, this.parsedLabelData.quantity, this.label && this.label.quantity)
        },
        displayPrintTime() {
            const fromParent = this.label && this.label.current_print_time
            if (fromParent !== undefined && fromParent !== null && fromParent !== '') {
                return fromParent
            }
            if (this.showBy === 'expiration_date' || this.showBy === 'end_date') {
                return this.label && this.label.end_date
            }
            return this.label && this.label.date
        },
        displayDateLabel() {
            return this.showBy === 'print_date' ? this.$t('label.print_date') : this.$t('label.traceability.cad')
        },
        serverNow() {
            const serverTimeDifference = Number(this.$store.getters['getServerTimeDifference']) || 0
            return moment().add(serverTimeDifference, 'minutes')
        },
        expirationState() {
            return resolveTraceabilityExpirationState(this.label, this.serverNow, this.parsedLabelData)
        },
        isExpired() {
            return this.expirationState.isExpired
        },
        isExpiresToday() {
            return this.expirationState.isExpiresToday
        }
    },
    methods: {
        formatDisplayMeasurement(value) {
            return formatMeasurementLabel(value, this.$i18n.locale, count => this.$tc('measurements.unit', count))
        },
        parseMoment(date) {
            return parseTraceabilityMoment(date)
        },
        parseDate(date) {
            return formatTraceabilityDate(date, this.dateLocale)
        },
        normalizeQuantity(...values) {
            const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '' && candidate !== 'NaN')
            const quantity = Number(value)
            return Number.isFinite(quantity) ? quantity : 0
        },
        sentenceCase(value) {
            const text = String(value || '')
            if (!text || text !== text.toLocaleUpperCase()) {
                return text
            }
            const lowercase = text.toLocaleLowerCase()
            return lowercase.charAt(0).toLocaleUpperCase() + lowercase.slice(1)
        },
        parseDateRelative(date) {
            return formatRelative(date)
        },
        discardLabel() {
            this.$emit('discard')
        }
    }
}
</script>

<style lang="scss" scoped>
.single-label {
    @include display-flex();
    @include align-items();
    @include justify-content(space-between);
    @include border-radius(5px);
    background-color: $neutro-light;
    box-sizing: border-box;
    width: 100%;
    padding: 10px;
    height: max-content;
    min-height: 110px;
    overflow: hidden;
    cursor: pointer;

    &.is-expired:not(.single-label--traceability) {
        border-left: $error-dark solid 6px;
    }

    .image {
        @include background($image: img('image_neutro.svg'), $size: contain, $position: center center);
        height: 80px;
        width: 80px;
        flex-shrink: 0;
    }

    .info {
        min-width: 0;
        width: calc(100% - 80px - 50px);

        &.is_printing {
            width: calc(100% - 80px - 10px);
        }

        .name {
            font-size: 16px;
            font-family: $text-bold;
            color: $neutro-s90;
            @include text-ellipsis();
        }

        .state {
            @include font-size(s);
            font-family: $text-light;
            text-transform: uppercase;
            @include text-ellipsis();
        }

        .meta {
            @include display-flex();
            @include align-items();
            @include justify-content(flex-start);

            .quantity,
            .date {
                color: $neutro-s50;
                font-size: 14px;
                font-family: $text-light;

                &::first-letter {
                    text-transform: uppercase;
                }

                &.expired {
                    font-family: $text;
                }
            }

            .quantity {
                @include background($image: img('label_neutro_s10.svg'), $size: 12px, $position: left center);
                padding-left: 15px;

                &::after {
                    width: 3px;
                    height: 3px;
                    background: $neutro-s20;
                    @include border-radius(100%);
                    content: ' ';
                    display: inline-block;
                    margin: 0 10px;
                    top: -2.5px;
                    position: relative;
                }
            }

            .date {
                @include text-ellipsis();
            }
        }
    }

    .action {
        @include background($image: img('close_main_ffffff.svg'), $color: $error-dark, $size: 18px, $position: center center);
        @include border-radius(4px);
        width: 36px;
        height: 36px;
        flex-shrink: 0;
        cursor: pointer;

        &.discard {
            @include background($image: img('close_main_ffffff.svg'), $color: $error-dark, $size: 18px, $position: center center);
        }

        &.preview {
            @include background($image: img('eye_preview_blue.svg'), $color: $main-t90, $size: 20px, $position: center center);
        }
    }
}

.single-label.single-label--traceability {
    @include align-items(center);
    @include border-radius(4px);
    position: relative;
    gap: 8px;
    height: max-content;
    min-height: 94px;
    max-height: none;
    padding: 8px;
    border: 1px solid $neutro-dark;
    background-color: $neutro-light;

    &.is-expired {
        border-color: $error-t70;

        .image {
            background-color: $error-t90;
        }

        .expiration-rail {
            background-color: $error-dark;
        }
    }

    &.is-expiring-today {
        border-color: $warning-t70;

        .image {
            background-color: $warning-t90;
        }

        .expiration-rail {
            background-color: $warning;
        }
    }

    .expiration-rail {
        align-self: stretch;
        width: 4px;
        flex-shrink: 0;
        background-color: $neutro;
    }

    .image {
        @include border-radius(4px);
        box-sizing: border-box;
        width: 58px;
        height: 58px;
        padding: 5px;
        background-origin: content-box;
        background-size: contain;
    }

    .info,
    .info.is_printing {
        @include display-flex();
        @include justify-content(flex-start);
        align-self: stretch;
        flex: 1 1 auto;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        width: auto;

        .name {
            color: $neutro-s90;
            font-family: $text-bold;
            font-size: 20px;
            line-height: 24px;
        }

        > .date {
            @include text-ellipsis();
            margin-top: auto;
            color: $neutro-s50;
            font-family: $text-light;
            font-size: 16px;
            line-height: 20px;
        }
    }

    .card-tags {
        @include display-flex();
        @include align-items(center);
        gap: 4px;
        min-width: 0;
        max-width: 100%;
        flex-wrap: wrap;
        overflow: visible;
    }

    .status-tag,
    .state-tag,
    .quantity-tag {
        @include align-items(center);
        @include border-radius(4px);
        display: inline-flex;
        box-sizing: border-box;
        height: 28px;
        white-space: nowrap;
    }

    .status-tag,
    .state-tag {
        min-width: 0;
        padding: 4px 8px;
        color: $neutro-s80;
        font-family: $text-light;
        font-size: 16px;
        line-height: 20px;
    }

    .status-tag {
        gap: 6px;
        flex-shrink: 0;

        &--expired {
            background-color: $error-t90;

            .status-tag__dot {
                background-color: $error-dark;
            }
        }

        &--today {
            background-color: $warning-t90;

            .status-tag__dot {
                background-color: $warning;
            }
        }

        &__dot {
            @include border-radius(100%);
            width: 10px;
            height: 10px;
            flex-shrink: 0;
        }
    }

    .state-tag {
        @include text-ellipsis();
        max-width: 100%;
        flex-shrink: 0;
        background-color: #edeff5;
    }

    .quantity-tag {
        gap: 4px;
        padding: 2px 5px;
        border: 1px solid $neutro-dark;
        color: $neutro-s80;
        font-family: $text-light;
        font-size: 16px;
        line-height: 20px;
        flex-shrink: 0;

        i {
            color: $neutro-s30;
            font-size: 18px;
            line-height: 20px;
        }

        .physical-label-icon {
            @include background($image: img('menu_label_dark.svg'), $position: center, $size: contain);
            width: 18px;
            height: 18px;
            flex-shrink: 0;
        }
    }

    .action.preview {
        padding-top: 10px;
        background-color: $main-t90;
        background-size: 20px;
    }
}
</style>

<style lang="scss" scoped>
.single-label.portrait:not(.single-label--traceability) {
    .info {
        .name {
            @include font-size(s);
        }

        .state,
        .meta .quantity,
        .meta .date {
            @include font-size(xs);
        }
    }
}

.single-label.single-label--traceability.portrait {
    height: max-content;

    .info,
    .info.is_printing {
        align-self: auto;
        height: max-content;
    }

    .info .name {
        font-size: 16px;
        line-height: 20px;
    }

    .info > .date,
    .status-tag,
    .state-tag,
    .quantity-tag {
        font-size: 14px;
        line-height: 18px;
    }

    .status-tag,
    .state-tag {
        padding-right: 6px;
        padding-left: 6px;
    }

    .card-tags {
        flex-wrap: wrap;
        overflow: visible;
    }

}
</style>
